import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  FlatList,
  StyleSheet,
  ActivityIndicator,
  TextInput,
  Pressable,
  KeyboardAvoidingView,
  Platform,
  Alert,
  useWindowDimensions,
  ScrollView,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useSetAtom } from 'jotai';
import { fetchPostById, deletePostById } from '../../services/postService';
import { fetchComments, insertComment } from '../../services/commentsService';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useRequireAuth } from '../../hooks/useRequireAuth';
import { headerSubtitleAtom } from '../../atoms';
import PostListItem from '../../components/PostListItem';
import CommentListItem from '../../components/CommentListItem';
import PixelContainer from '../../components/PixelContainer';
import ConfirmDialog from '../../components/ConfirmDialog';
import LeftSidebar from '../../components/LeftSidebar';
import ZoneSidebar from '../../components/ZoneSidebar';
import { Comment } from '../../types';
import { fontStyle } from '../../config/fonts';
import { useTheme as useThemeHook } from '../../contexts/ThemeContext';

const WEB_SIDEBAR_BREAKPOINT = 1024; // Show sidebars on screens wider than this

export default function PostDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { t } = useTranslation();
  const router = useRouter();
  const { user } = useAuth();
  const { colors } = useThemeHook();
  const { width: windowWidth } = useWindowDimensions();
  const showSidebars = Platform.OS === 'web' && windowWidth >= WEB_SIDEBAR_BREAKPOINT;
  const { requireAuth } = useRequireAuth();
  const queryClient = useQueryClient();
  const inputRef = useRef<TextInput>(null);
  const setHeaderSubtitle = useSetAtom(headerSubtitleAtom);

  const [commentText, setCommentText] = useState('');
  const [replyToId, setReplyToId] = useState<string | null>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Fetch post - use placeholderData to show cached post from home screen immediately
  // Skip comments count since we fetch comments separately
  const {
    data: post,
    isLoading: isLoadingPost,
    isError: isPostError,
  } = useQuery({
    queryKey: ['post', id],
    queryFn: () => fetchPostById(id!, { skipCommentsCount: true }),
    enabled: !!id,
    staleTime: 1000 * 60 * 2, // 2 minutes - prevent refetching on navigation
    // Get post from home screen cache to avoid loading state
    placeholderData: () => {
      const postsCache = queryClient.getQueryData<{ pages: { posts: any[] }[] }>(['posts']);
      if (postsCache?.pages) {
        for (const page of postsCache.pages) {
          const cachedPost = page.posts.find((p: any) => p.id === id);
          if (cachedPost) return cachedPost;
        }
      }
      return undefined;
    },
  });

  // Update header subtitle with zone name
  useEffect(() => {
    if (post?.group?.name) {
      setHeaderSubtitle(post.group.name);
    }
    return () => {
      setHeaderSubtitle(null);
    };
  }, [post?.group?.name, setHeaderSubtitle]);

  // Fetch comments - use staleTime to prevent refetching on re-navigation
  const {
    data: comments,
    isLoading: isLoadingComments,
  } = useQuery({
    queryKey: ['comments', id],
    queryFn: () => fetchComments(id!),
    enabled: !!id,
    staleTime: 1000 * 60, // 1 minute - comments may update more frequently
  });

  // Delete post mutation
  const deletePostMutation = useMutation({
    mutationFn: () => deletePostById(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      router.back();
    },
    onError: () => {
      Alert.alert(t('common.error'), t('errors.deleteFailed'));
    },
  });

  // Add comment mutation
  const addCommentMutation = useMutation({
    mutationFn: () => {
      if (!user) throw new Error('Not authenticated');
      return insertComment(
        {
          comment: commentText.trim(),
          postId: id!,
          parentId: replyToId || undefined,
        },
        user.uid
      );
    },
    onSuccess: () => {
      setCommentText('');
      setReplyToId(null);
      inputRef.current?.blur();
      queryClient.invalidateQueries({ queryKey: ['comments', id] });
      queryClient.invalidateQueries({ queryKey: ['post', id] });
    },
    onError: () => {
      Alert.alert(t('common.error'), t('errors.commentFailed'));
    },
  });

  const handleDeletePost = () => {
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    setShowDeleteDialog(false);
    deletePostMutation.mutate();
  };

  const handleReply = (parentId: string) => {
    if (!requireAuth()) return;
    setReplyToId(parentId);
    inputRef.current?.focus();
  };

  const handleSubmitComment = () => {
    if (!requireAuth()) return;
    if (!commentText.trim()) return;
    addCommentMutation.mutate();
  };

  const handleCancelReply = () => {
    setReplyToId(null);
  };

  const renderHeader = () => {
    if (!post) return null;

    return (
      <PostListItem
        post={post}
        isDetailedPost
        onMorePress={handleDeletePost}
      />
    );
  };

  const renderComment = ({ item }: { item: Comment }) => (
    <CommentListItem comment={item} onReply={handleReply} />
  );

  const renderEmpty = () => {
    if (isLoadingComments) {
      return (
        <View style={styles.commentsLoading}>
          <ActivityIndicator size="small" color={colors.primary} />
          <Text style={[styles.loadingText, fontStyle, { color: colors.textSecondary }]}>
            {t('common.loading') || 'Loading comments...'}
          </Text>
        </View>
      );
    }
    return (
      <View style={styles.emptyComments}>
        <Text style={[styles.emptyText, fontStyle, { color: colors.textSecondary }]}>{t('post.noComments')}</Text>
      </View>
    );
  };

  if (isLoadingPost) {
    return (
      <View style={[styles.centered, { backgroundColor: colors.background }]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (isPostError || !post) {
    return (
      <View style={[styles.centered, { backgroundColor: colors.background }]}>
        <Text style={[styles.errorText, fontStyle, { color: colors.error }]}>{t('errors.loadFailed')}</Text>
      </View>
    );
  }

  // Main content component (extracted to avoid duplication)
  const renderMainContent = () => (
    <View style={[styles.mainContent, !showSidebars && styles.mainContentFullWidth]}>
      <FlatList
        data={comments || []}
        renderItem={renderComment}
        keyExtractor={(item) => item.id}
        ListHeaderComponent={renderHeader()}
        ListEmptyComponent={renderEmpty()}
        contentContainerStyle={styles.listContent}
        scrollEnabled={!showSidebars}
        nestedScrollEnabled={!showSidebars}
      />

      {/* Comment Input */}
      <View style={[styles.inputContainer, { borderTopColor: colors.border, backgroundColor: colors.background }]}>
        {replyToId && (
          <View style={[styles.replyIndicator, { backgroundColor: colors.surface }]}>
            <Text style={[styles.replyText, fontStyle, { color: colors.textSecondary }]}>{t('post.reply')}</Text>
            <Pressable onPress={handleCancelReply}>
              <Ionicons name="close" size={18} color={colors.textSecondary} />
            </Pressable>
          </View>
        )}
        <View style={styles.inputRow}>
          <PixelContainer backgroundColor={colors.surface} cornerSize={4} style={styles.inputWrapper}>
            <TextInput
              ref={inputRef}
              style={[styles.input, fontStyle, { color: colors.text }]}
              placeholder={t('post.addComment')}
              placeholderTextColor={colors.textSecondary}
              value={commentText}
              onChangeText={setCommentText}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setIsInputFocused(false)}
              multiline
            />
          </PixelContainer>
          {(isInputFocused || commentText.length > 0) && (
            <Pressable
              onPress={handleSubmitComment}
              style={[
                styles.sendButton,
                !commentText.trim() && styles.sendButtonDisabled,
              ]}
              disabled={!commentText.trim() || addCommentMutation.isPending}
            >
              <Ionicons
                name="send"
                size={20}
                color={commentText.trim() ? colors.primary : colors.textSecondary}
              />
            </Pressable>
          )}
        </View>
      </View>
    </View>
  );

  return (
    <>
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]} edges={['bottom']}>
        <KeyboardAvoidingView
          style={styles.keyboardView}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          keyboardVerticalOffset={100}
        >
          {showSidebars ? (
            // Web layout with sidebars - parent scroll, sticky sidebars, centered content
            <ScrollView
              style={styles.webScrollContainer}
              contentContainerStyle={styles.webScrollContent}
              showsVerticalScrollIndicator={true}
            >
              <View style={styles.webLayout}>
                {/* Left Sidebar - Sticky */}
                <View style={styles.stickySidebar}>
                  <LeftSidebar />
                </View>

                {/* Main Content + Zone Sidebar */}
                <View style={styles.webContentRow}>
                  {renderMainContent()}
                  <ZoneSidebar zone={post?.group} />
                </View>
              </View>
            </ScrollView>
          ) : (
            // Mobile layout (no sidebars)
            <View style={styles.contentWrapper}>
              {renderMainContent()}
            </View>
          )}
        </KeyboardAvoidingView>
      </SafeAreaView>

      {/* Delete confirmation dialog for web */}
      <ConfirmDialog
        visible={showDeleteDialog}
        title={t('post.deletePost')}
        message={t('post.deleteWarning')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        confirmDestructive
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteDialog(false)}
      />
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  keyboardView: {
    flex: 1,
  },
  webScrollContainer: {
    flex: 1,
  },
  webScrollContent: {
    flexGrow: 1,
    alignItems: 'center',
  },
  webLayout: {
    flexDirection: 'row',
    width: '100%',
    maxWidth: 1220, // 240 (left) + 680 (center) + 300 (zone sidebar)
  },
  stickySidebar: {
    ...(Platform.OS === 'web' && {
      // @ts-ignore - 'sticky' is valid on web but not in RN types
      position: 'sticky',
      top: 0,
      alignSelf: 'flex-start',
      height: 'auto',
    }),
  },
  contentWrapper: {
    flex: 1,
    ...(Platform.OS === 'web' && {
      maxWidth: 680,
      width: '100%',
      alignSelf: 'center',
    }),
  },
  webContentRow: {
    flex: 1,
    flexDirection: 'row',
    minHeight: '100%',
  },
  mainContent: {
    flex: 1,
    maxWidth: 680,
    minHeight: '100%',
  },
  mainContentFullWidth: {
    maxWidth: undefined,
    width: '100%',
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontSize: 16,
  },
  listContent: {
    paddingBottom: 16,
  },
  emptyComments: {
    padding: 32,
    alignItems: 'center',
  },
  commentsLoading: {
    padding: 32,
    alignItems: 'center',
    gap: 12,
  },
  loadingText: {
    fontSize: 13,
  },
  emptyText: {
    fontSize: 14,
  },
  inputContainer: {
    borderTopWidth: 1,
  },
  replyIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  replyText: {
    fontSize: 12,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    padding: 12,
    gap: 8,
  },
  inputWrapper: {
    flex: 1,
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  input: {
    fontSize: 14,
    maxHeight: 100,
  },
  sendButton: {
    padding: 8,
  },
  sendButtonDisabled: {
    opacity: 0.5,
  },
});
